import { notificationsApi, Notification } from './api/notifications.api';
import { Bell, CheckCircle, Info, AlertTriangle, XCircle } from 'lucide-react';

/**
 * Notification Service
 * Service de gestion des notifications avec formatage et helpers
 */
export class NotificationsService {
  /**
   * Récupérer toutes les notifications
   */
  static async getNotifications(): Promise<Notification[]> {
    return notificationsApi.getNotifications();
  }

  /**
   * Récupérer les notifications non lues uniquement
   */
  static async getUnreadNotifications(): Promise<Notification[]> {
    const notifications = await notificationsApi.getNotifications();
    return notifications.filter(n => !n.read);
  }

  /**
   * Récupérer le nombre de notifications non lues
   */
  static async getUnreadCount(): Promise<number> {
    return notificationsApi.getUnreadCount();
  }

  /**
   * Marquer une notification comme lue
   */
  static async markAsRead(id: string): Promise<void> {
    return notificationsApi.markAsRead(id);
  }

  /**
   * Marquer toutes les notifications comme lues
   */
  static async markAllAsRead(): Promise<void> {
    return notificationsApi.markAllAsRead();
  }

  /**
   * Supprimer toutes les notifications
   */
  static async clearAll(): Promise<void> {
    return notificationsApi.clearAll();
  }

  /**
   * Créer une notification (pour tests)
   */
  static async create(data: Partial<Notification>): Promise<Notification> {
    return notificationsApi.create(data);
  }

  /**
   * Formater une notification avec icône et couleur
   */
  static formatNotification(notification: Notification): {
    icon: string;
    color: string;
    bgColor: string;
  } {
    switch (notification.type) {
      case 'success':
        return {
          icon: '✓',
          color: 'text-green-600',
          bgColor: 'bg-green-50',
        };
      case 'error':
        return {
          icon: '✗',
          color: 'text-red-600',
          bgColor: 'bg-red-50',
        };
      case 'warning':
        return {
          icon: '⚠',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
        };
      case 'info':
      default:
        return {
          icon: 'ℹ',
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
        };
    }
  }

  /**
   * Obtenir l'icône Lucide pour un type de notification
   */
  static getIconComponent(type: Notification['type']) {
    switch (type) {
      case 'success':
        return CheckCircle;
      case 'error':
        return XCircle;
      case 'warning':
        return AlertTriangle;
      case 'info':
      default:
        return Info;
    }
  }

  /**
   * Formater le temps relatif (ex: "il y a 5 minutes")
   */
  static getRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) {
      return 'À l\'instant';
    } else if (diffMinutes < 60) {
      return `il y a ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
    } else if (diffHours < 24) {
      return `il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
    } else if (diffDays < 7) {
      return `il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
    } else {
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      });
    }
  }

  /**
   * Grouper les notifications par date
   */
  static groupByDate(notifications: Notification[]): Record<string, Notification[]> {
    const groups: Record<string, Notification[]> = {
      "Aujourd'hui": [],
      'Hier': [],
      'Cette semaine': [],
      'Plus ancien': [],
    };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    notifications.forEach(notification => {
      const notifDate = new Date(notification.created_at);
      const notifDay = new Date(
        notifDate.getFullYear(),
        notifDate.getMonth(),
        notifDate.getDate()
      );

      if (notifDay.getTime() === today.getTime()) {
        groups["Aujourd'hui"].push(notification);
      } else if (notifDay.getTime() === yesterday.getTime()) {
        groups['Hier'].push(notification);
      } else if (notifDay.getTime() >= weekAgo.getTime()) {
        groups['Cette semaine'].push(notification);
      } else {
        groups['Plus ancien'].push(notification);
      }
    });

    // Retirer les groupes vides
    Object.keys(groups).forEach(key => {
      if (groups[key].length === 0) {
        delete groups[key];
      }
    });

    return groups;
  }

  /**
   * Trier les notifications par date (plus récentes en premier)
   */
  static sortByDate(notifications: Notification[]): Notification[] {
    return [...notifications].sort((a, b) => {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }

  /**
   * Filtrer les notifications par type
   */
  static filterByType(
    notifications: Notification[],
    type: Notification['type']
  ): Notification[] {
    return notifications.filter(n => n.type === type);
  }

  /**
   * Rechercher dans les notifications
   */
  static search(notifications: Notification[], query: string): Notification[] {
    const lowerQuery = query.toLowerCase();
    return notifications.filter(
      n =>
        n.title.toLowerCase().includes(lowerQuery) ||
        n.message.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Obtenir les statistiques des notifications
   */
  static getStats(notifications: Notification[]): {
    total: number;
    unread: number;
    byType: Record<Notification['type'], number>;
  } {
    const stats = {
      total: notifications.length,
      unread: notifications.filter(n => !n.read).length,
      byType: {
        success: 0,
        info: 0,
        warning: 0,
        error: 0,
      } as Record<Notification['type'], number>,
    };

    notifications.forEach(n => {
      stats.byType[n.type] = (stats.byType[n.type] || 0) + 1;
    });

    return stats;
  }
}

export default NotificationsService;
